import React, { useMemo, useState } from "react";
import { formatCurrency } from "./InventoryUtils";
import { ManufacturingPendingView } from "./ManufacturingPendingView";
import { ColorBadge } from "@/components/ColorBadge";
import { ProductAudit } from "./ProductAudit";

const matchesStockPendingFilter = (stock, pending, mode) => {
  const stockValue = Number(stock) || 0;
  const pendingValue = Number(pending) || 0;

  switch (mode) {
    case "stock_only":
      return stockValue > 0;
    case "pending_only":
      return pendingValue > 0;
    case "pending_with_stock":
      return pendingValue > 0 && stockValue > 0;
    case "pending_without_stock":
      return pendingValue > 0 && stockValue <= 0;
    case "relevant":
    default:
      return stockValue > 0 || pendingValue > 0;
  }
};

// Componente memoizado para las filas de la tabla
const InventoryRow = React.memo(({ product, inventoryItem, size, color, stockPendingFilterMode, getStockClass, openAudit }) => {
  const stock = Number(inventoryItem?.quantity) || 0;
  const pending = Number(inventoryItem?.pending_orders) || 0;
  const available = stock - pending;

  if (!matchesStockPendingFilter(stock, pending, stockPendingFilterMode)) return null;

  // Manejar imágenes de manera segura
  const productImages = product.images || [];
  const mainImageIndex = product.main_image_index || 0;
  const imageUrl = productImages[mainImageIndex];

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        {imageUrl && (
          <div className="w-16 h-16 flex-shrink-0">
            <img
              src={imageUrl}
              alt={product.description}
              className="w-full h-full object-cover rounded"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-sm">{product.code}</td>
      <td className="px-4 py-3 text-sm">{product.description}</td>
      <td className="px-4 py-3 text-sm">{inventoryItem?.size || size || ''}</td>
      <td className="px-4 py-3 text-sm">
        {(inventoryItem?.color || color) && <ColorBadge color={inventoryItem?.color || color} />}
      </td>
      <td className={`px-4 py-3 text-sm text-right ${getStockClass(stock)}`}>
        {stock}
      </td>
      <td className="px-4 py-3 text-sm text-right text-blue-600">
        {pending}
      </td>
      <td className="px-4 py-3 text-sm text-right text-indigo-600 font-medium">
        {available}
      </td>
      <td className="px-4 py-3 text-sm text-right">
        {formatCurrency(stock * product.price)}
      </td>
      <td className="px-4 py-3 text-sm text-right">
        <button
          className="px-2 py-1 text-xs rounded bg-gray-800 text-white hover:bg-gray-700"
          onClick={() => openAudit(product.id)}
          title="Auditar producto"
        >
          Auditar
        </button>
      </td>
    </tr>
  );
});

// Componente memoizado para las filas resumidas
const SummaryRow = React.memo(({ product, productInventory, stockPendingFilterMode, getStockClass, openAudit }) => {
  const totalStock = productInventory.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const totalPending = productInventory.reduce((sum, item) => sum + (Number(item.pending_orders) || 0), 0);

  if (!matchesStockPendingFilter(totalStock, totalPending, stockPendingFilterMode)) return null;

  // Manejar imágenes de manera segura
  const productImages = product.images || [];
  const mainImageIndex = product.main_image_index || 0;
  const imageUrl = productImages[mainImageIndex];

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        {imageUrl && (
          <div className="w-16 h-16 flex-shrink-0">
            <img
              src={imageUrl}
              alt={product.description}
              className="w-full h-full object-cover rounded"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-sm">{product.code}</td>
      <td className="px-4 py-3 text-sm">{product.description}</td>
      <td className={`px-4 py-3 text-sm text-right ${getStockClass(totalStock)}`}>
        {totalStock}
      </td>
      <td className="px-4 py-3 text-sm text-right text-blue-600">
        {totalPending}
      </td>
      <td className="px-4 py-3 text-sm text-right">
        {formatCurrency(totalStock * product.price)}
      </td>
      <td className="px-4 py-3 text-sm text-right">
        <button
          className="px-2 py-1 text-xs rounded bg-gray-800 text-white hover:bg-gray-700"
          onClick={() => openAudit(product.id)}
          title="Auditar producto"
        >
          Auditar
        </button>
      </td>
    </tr>
  );
});

export const InventoryView = React.memo(({ inventory, filteredInventory, products, orders, viewMode, stockPendingFilterMode, productsNeedingManufacturing, filter }) => {
  const [auditProductId, setAuditProductId] = useState("");
  const [showAudit, setShowAudit] = useState(false);
  // Debug: Verificar que los productos tienen imágenes
  console.log('🔍 Debug - Productos recibidos:', products?.length || 0);
  if (products && products.length > 0) {
    const sampleProduct = products[0];
    console.log('🔍 Debug - Producto de ejemplo:', {
      id: sampleProduct.id,
      code: sampleProduct.code,
      hasImages: !!sampleProduct.images,
      imagesLength: sampleProduct.images?.length || 0,
      mainImageIndex: sampleProduct.main_image_index,
      firstImage: sampleProduct.images?.[0]?.substring(0, 50) + '...'
    });
  }

  // TODOS LOS HOOKS DEBEN EJECUTARSE SIEMPRE, SIN IMPORTAR EL viewMode
  const getStockClass = useMemo(() => (stock) => {
    if (stock <= 0) return "text-red-600 font-medium";
    if (stock <= 5) return "text-yellow-600 font-medium";
    return "text-green-600 font-medium";
  }, []);

  // Crear un mapa de inventario para acceso rápido
  const inventoryMap = useMemo(() => {
    const map = {};
    inventory.forEach(item => {
      const key = `${item.product_id}::${item.size}::${item.color}`;
      map[key] = {
        stock: item.quantity || 0,
        pending: item.pending_orders || 0
      };
    });
    return map;
  }, [inventory]);

  // Debug opcional por producto
  try {
    const debugId = (typeof localStorage !== 'undefined' && localStorage.getItem('inventoryDebugId')) || 'a5006d2b-8a31-444b-8c13-99fa2798ce08';
    if (debugId) {
      const rows = inventory.filter(i => String(i.product_id) === String(debugId));
      const pendingSum = rows.reduce((s, it) => s + (Number(it.pending_orders) || 0), 0);
      const qtySum = rows.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
      const prod = products.find(p => String(p.id) === String(debugId));
      // eslint-disable-next-line no-console
      console.log('🔎 INV-DEBUG view', {
        debugId,
        productCode: prod?.code,
        rows: rows.length,
        qtySum,
        pendingSum,
        sample: rows.slice(0, 8)
      });
    }
  } catch (_) {}

  // Memoizar los totales para evitar recálculos
  const totals = useMemo(() => {
    const totalStock = filteredInventory.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalPending = filteredInventory.reduce((sum, item) => sum + (item.pending_orders || 0), 0);
    const totalValue = filteredInventory.reduce((sum, item) => {
      const product = products.find(p => p.id === item.product_id);
      return sum + ((item.quantity || 0) * (product?.price || 0));
    }, 0);

    return { totalStock, totalPending, totalValue };
  }, [filteredInventory, products]);

  // Memoizar productos agrupados por inventario con filtro
  const productsWithInventory = useMemo(() => {
    let filteredProducts = products;
    let filteredInventory = inventory;

    // Aplicar filtro si existe
    if (filter) {
      if (filter.type === 'product') {
        // Filtrar solo por producto
        filteredProducts = products.filter(product => product.id === filter.productId);
        filteredInventory = inventory.filter(item => item.product_id === filter.productId);
      } else if (filter.type === 'specific') {
        // Filtrar por producto, talla y color específicos
        filteredProducts = products.filter(product => product.id === filter.productId);
        filteredInventory = inventory.filter(item => 
          item.product_id === filter.productId && 
          item.size === filter.size && 
          item.color === filter.color
        );
      }
    }

    return filteredProducts.map(product => ({
      ...product,
      inventory: filteredInventory.filter(item => item.product_id === product.id)
    }));
  }, [products, inventory, filter]);

  // Vista detallada optimizada - SIEMPRE se ejecuta
  const detailedRows = useMemo(() => {
    const rows = [];
    let filteredProducts = products;
    let filteredInventory = inventory;

    // Aplicar filtro si existe
    if (filter) {
      if (filter.type === 'product') {
        // Filtrar solo por producto
        filteredProducts = products.filter(product => product.id === filter.productId);
        filteredInventory = inventory.filter(item => item.product_id === filter.productId);
      } else if (filter.type === 'specific') {
        // Filtrar por producto, talla y color específicos
        filteredProducts = products.filter(product => product.id === filter.productId);
        filteredInventory = inventory.filter(item => 
          item.product_id === filter.productId && 
          item.size === filter.size && 
          item.color === filter.color
        );
      }
    }

    filteredProducts.forEach((product) => {
      product.sizes?.forEach((size) => {
        product.colors?.forEach((color) => {
          const normalizedSize = String(size);
          const normalizedColor = String(color);
          const inventoryItem = filteredInventory.find(
            item => item.product_id === product.id && 
                   String(item.size) === normalizedSize && 
                   String(item.color) === normalizedColor
          );
          
          rows.push({
            product,
            inventoryItem,
            size,
            color,
            key: `${product.id}::${size}::${color}`
          });
        });
      });
    });
    return rows;
  }, [products, inventory, filter]);

  // AHORA RENDERIZAMOS SEGÚN EL viewMode, PERO TODOS LOS HOOKS YA SE EJECUTARON
  const auditPanel = null;
  const auditOverlay = showAudit ? (
    <div className="w-full border rounded p-3">
      <ProductAudit productId={auditProductId} onClose={() => setShowAudit(false)} />
    </div>
  ) : null;

  if (viewMode === "summary") {
    return (
      <div className="space-y-4">
        {auditOverlay}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="flex justify-end gap-6 px-4 py-2 text-sm text-gray-700 bg-gray-50 border-b">
          <div>
            <span className="font-medium">Stock total:</span>{" "}
            {totals.totalStock}
          </div>
          <div>
            <span className="font-medium">Pendientes:</span>{" "}
            {totals.totalPending}
          </div>
          <div>
            <span className="font-medium">Valor inventario:</span>{" "}
            {formatCurrency(totals.totalValue)}
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Imagen</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Código</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Descripción</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Stock Total</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Pedidos Pendientes</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Valor Total</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Auditar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {productsWithInventory.map((product) => (
              <SummaryRow
                key={product.id}
                product={product}
                productInventory={product.inventory}
                stockPendingFilterMode={stockPendingFilterMode}
                getStockClass={getStockClass}
                openAudit={(id) => { setAuditProductId(id); setShowAudit(true); }}
              />
            ))}
          </tbody>
          <tfoot className="bg-gray-100">
            <tr>
              <td colSpan={3} className="px-4 py-3 text-sm font-medium text-right">Totales:</td>
              <td className="px-4 py-3 text-sm text-right font-bold">
                {totals.totalStock}
              </td>
              <td className="px-4 py-3 text-sm text-right font-bold">
                {totals.totalPending}
              </td>
              <td className="px-4 py-3 text-sm text-right font-bold">
                {formatCurrency(totals.totalValue)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      </div>
    );
  }

  if (viewMode === "manufacturing") {
    return (
      <div className="space-y-4">
        {auditOverlay}
        <ManufacturingPendingView
          productsNeedingManufacturing={productsNeedingManufacturing}
          inventory={inventory}
          filter={filter}
          stockPendingFilterMode={stockPendingFilterMode}
        />
      </div>
    );
  }

  // Vista detallada (default)
  return (
    <div className="space-y-4">
      {auditOverlay}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Imagen</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Código</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Descripción</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Talla</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Color</th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Stock</th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Pedidos Pendientes</th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Disponible</th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Valor</th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Auditar</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {detailedRows.map(({ product, inventoryItem, size, color, key }) => (
            <InventoryRow
              key={key}
              product={product}
              inventoryItem={inventoryItem}
              size={size}
              color={color}
              stockPendingFilterMode={stockPendingFilterMode}
              getStockClass={getStockClass}
              openAudit={(id) => { setAuditProductId(id); setShowAudit(true); }}
            />
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
});
